import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { apiService } from '@services/api';
import { authStorage } from '@lib/storage';
import type { AlertNotification } from '@/types/dashboard';
import './NotificationList.css';

interface NotificationListProps {
  configValueId?: number;
  position: { left: number; top: number; bellCenterX?: number; bellCenterY?: number };
  onClose: () => void;
  onSelectNotification: (notification: AlertNotification, fromSummary?: boolean) => void;
  onRefreshAlerts?: () => void;
  onDecrementAlertCount?: (configValueId: number, amount?: number) => void;
  showArrow?: boolean;
}

const PAGE_SIZE = 12;

function removeHrefs(html: string): string
{
  if (!html) return html;
  return html.replace(/\s*href\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');
}

const PANEL_WIDTH = 350;
const PANEL_MARGIN = 8;

const clampLeft = (left: number) =>
  Math.max(PANEL_MARGIN, Math.min(left, window.innerWidth - PANEL_WIDTH - PANEL_MARGIN));

export const NotificationList: React.FC<NotificationListProps> = ({
  configValueId = 0,
  position,
  onClose,
  onSelectNotification,
  onRefreshAlerts,
  onDecrementAlertCount,
  showArrow = true,
}) =>
{
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<AlertNotification[]>([]);
  const [page, setPage] = useState(1);
  const [noMoreItems, setNoMoreItems] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [important, setImportant] = useState(false);
  const [textSearch, setTextSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [openHistoryAlert, setOpenHistoryAlert] = useState<AlertNotification | null>(null);
  const [historyNotifications, setHistoryNotifications] = useState<AlertNotification[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPosition, setHistoryPosition] = useState<{
    left: number;
    top: number;
    height: number;
  }>({ left: 0, top: 0, height: 0 });

  const adjustedLeft = clampLeft(position.left);
  const [adjustedTop, setAdjustedTop] = useState(position.top);
  const [arrowOffsetY, setArrowOffsetY] = useState(14);

  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const positionRef = useRef(position);
  positionRef.current = position;

  useEffect(() =>
  {
    const handleMouseDown = (e: MouseEvent) =>
    {
      const target = e.target as Node;
      // The history panel is rendered outside containerRef, so it must be treated as "inside"
      if (containerRef.current?.contains(target) || historyPanelRef.current?.contains(target))
      {
        return;
      }
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  const recalcPosition = useCallback(() =>
  {
    const el = containerRef.current;
    if (!el) return;
    const pos = positionRef.current;
    const maxTop = window.innerHeight - el.offsetHeight - PANEL_MARGIN;
    const newTop = pos.top <= maxTop ? pos.top : Math.max(PANEL_MARGIN, maxTop);
    setAdjustedTop(newTop);
    const bellY = pos.bellCenterY ?? newTop + 14;
    const rawOffset = bellY - newTop;
    setArrowOffsetY(Math.max(10, Math.min(rawOffset, el.offsetHeight - 10)));
  }, []);

  useLayoutEffect(() =>
  {
    const el = containerRef.current;
    if (!el) return;
    recalcPosition();
    const ro = new ResizeObserver(recalcPosition);
    ro.observe(el);
    return () => ro.disconnect();
  }, [recalcPosition]);

  const getTimeZone = () => -(new Date().getTimezoneOffset() / 60);

  const fetchNotifications = useCallback(
    async (
      pageNum: number,
      pageSize: number,
      onlyRead: number,
      search: string,
      imp: number,
      append = false,
    ) =>
    {
      try
      {
        setLoading(true);
        const tz = getTimeZone();
        const data = await apiService.get<AlertNotification[]>(
          `alert/GetAllAlertsByPage?configValueId=${configValueId}&Page=${pageNum}&PageSize=${pageSize}&OnlyRead=${onlyRead}&timeZone=${tz}&textSearch=${encodeURIComponent(search)}&important=${imp}`,
        );

        if (append)
        {
          setNotifications(prev => [...prev, ...data]);
        }
        else
        {
          setNotifications(data);
        }
        setNoMoreItems(data.length < pageSize);
        return data;
      }
      catch (error)
      {
        console.error('Error fetching notifications:', error);
        return [];
      }
      finally
      {
        setLoading(false);
      }
    },
    [configValueId],
  );

  useEffect(() =>
  {
    fetchNotifications(1, PAGE_SIZE, 0, '', 0);
    // Refresh the bell counter immediately when the panel opens
    onRefreshAlerts?.();
  }, [fetchNotifications, onRefreshAlerts]);

  const handleScroll = useCallback(() =>
  {
    const el = listRef.current;
    if (!el || noMoreItems || loading) return;

    if (scrollTimeoutRef.current) return;

    scrollTimeoutRef.current = setTimeout(() =>
    {
      scrollTimeoutRef.current = null;
      if (!el) return;

      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - 50)
      {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchNotifications(
          nextPage,
          PAGE_SIZE,
          readOnly ? 1 : 0,
          textSearch,
          important ? 1 : 0,
          true,
        );
      }
    }, 300);
  }, [page, readOnly, important, textSearch, noMoreItems, loading, fetchNotifications]);

  useEffect(() =>
  {
    const el = listRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll);
    return () =>
    {
      el.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current)
      {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const resetAndFetch = useCallback(
    (onlyRead: number, search: string, imp: number) =>
    {
      setPage(1);
      if (listRef.current) listRef.current.scrollTop = 0;
      fetchNotifications(1, PAGE_SIZE, onlyRead, search, imp);
    },
    [fetchNotifications],
  );

  const handleReadOnlyChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const checked = e.target.checked;
    setReadOnly(checked);
    resetAndFetch(checked ? 1 : 0, textSearch, important ? 1 : 0);
  };

  const handleImportantChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const checked = e.target.checked;
    setImportant(checked);
    resetAndFetch(readOnly ? 1 : 0, textSearch, checked ? 1 : 0);
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) =>
  {
    e.preventDefault();
    try
    {
      const personId = authStorage.getPersonId() || '0';
      await apiService.put('alert/MarkAllAsViewed', {
        ConfigValueId: configValueId,
        PersonId: personId,
        Important: important,
      });
      setNotifications(prev => prev.map(n => ({ ...n, Viewed: true })));
      onRefreshAlerts?.();
    }
    catch (error)
    {
      console.error('Error marking all as read:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const value = e.target.value;
    setTextSearch(value);
    if (value.length === 0 && !loading)
    {
      resetAndFetch(readOnly ? 1 : 0, '', important ? 1 : 0);
    }
  };

  const handleSearchClear = () =>
  {
    if (loading) return;
    setTextSearch('');
    resetAndFetch(readOnly ? 1 : 0, '', important ? 1 : 0);
  };

  const handleSearchSubmit = () =>
  {
    // Ignore repeated clicks / Enter presses while a request is in flight
    if (loading) return;
    resetAndFetch(readOnly ? 1 : 0, textSearch, important ? 1 : 0);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
  {
    if (e.key === 'Enter')
    {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const navigateFromBody = useCallback((body: string): boolean =>
  {
    const doc = new DOMParser().parseFromString(body, 'text/html');
    const linkEl = doc.querySelector('#labela') as HTMLAnchorElement | null;
    const href = linkEl?.getAttribute('href') ?? '';
    const hashIndex = href.indexOf('#/');
    if (hashIndex === -1) return false;
    const route = href.slice(hashIndex + 1);
    if (!route.includes('VacationsApproval') && !route.includes('PTOReport')) return false;
    navigate(route);
    return true;
  }, [navigate]);

  const markAsRead = async (notification: AlertNotification) =>
  {
    // For conversation-based notifications, skip if already viewed.
    // For non-conversation notifications (e.g. Users), always call UpdateViewed.
    if (notification.Viewed) return;

    setNotifications(prev =>
      prev.map(n => (n.AlertID === notification.AlertID ? { ...n, Viewed: true } : n)),
    );
    setHistoryNotifications(prev =>
      prev.map(n => (n.AlertID === notification.AlertID ? { ...n, Viewed: true } : n)),
    );

    try
    {
      if (notification.ConversationId)
      {
        await apiService.put(
          `notification/SetViewedByConversationId?conversationId=${notification.ConversationId}`,
        );
      }
      await apiService.put('alert/UpdateViewed', { ...notification, Viewed: true });
    }
    catch (error)
    {
      console.error('Error marking notification as viewed:', error);
    }
  };

  const handleNotificationClick = async (notification: AlertNotification, e: React.MouseEvent) =>
  {
    // Prevent the body's <a href> from navigating so only the InfoWindow opens.
    e.preventDefault();
    e.stopPropagation();
    onSelectNotification(notification);

    if (notification.ConfigValueId === 15)
    {
      navigateFromBody(notification.Body);
    }

    // Only decrement if notification is actually unread
    if (!notification.Viewed)
    {
      // Count how many unread notifications this conversation has
      let decrementAmount = 1;
      if (notification.ConversationId)
      {
        // Count unread notifications with same ConversationId
        decrementAmount = notifications.filter(
          n => n.ConversationId === notification.ConversationId && !n.Viewed,
        ).length;
      }

      // Use the notification's own ConfigValueId instead of the NotificationList's configValueId
      const targetConfigValueId = notification.ConfigValueId;

      // Optimistic update FIRST - decrement immediately for instant feedback
      onDecrementAlertCount?.(targetConfigValueId, decrementAmount);

      // THEN call API in background (don't await - let it run async)
      markAsRead(notification);
    }
    else
    {
      // Already viewed, just select it
      markAsRead(notification);
    }
  };

  const openAlertHistory = async (notification: AlertNotification, e: React.MouseEvent) =>
  {
    e.preventDefault();
    e.stopPropagation();

    // Toggle: close if same alert clicked again
    if (openHistoryAlert?.AlertID === notification.AlertID)
    {
      setOpenHistoryAlert(null);
      setHistoryNotifications([]);
      return;
    }

    // Calculate position: place the panel flush to the right of the main container, same height
    const containerEl = containerRef.current;
    if (containerEl)
    {
      const containerRect = containerEl.getBoundingClientRect();
      setHistoryPosition({
        left: containerRect.right - 1,
        top: containerRect.top,
        height: containerRect.height,
      });
    }

    setOpenHistoryAlert(notification);
    setHistoryNotifications([]);
    setHistoryLoading(true);

    try
    {
      const alerts = await apiService.get<AlertNotification[]>(
        `alert/GetAllAlertsByConversationId?conversationId=${notification.ConversationId}`,
      );
      if (alerts.length > 0)
      {
        const firstViewed = alerts[0]?.Viewed ?? false;
        const processed = alerts.map((a, i) => ({
          ...a,
          Viewed: i === 0 ? firstViewed : true,
        }));
        setHistoryNotifications(processed);
      }
    }
    catch (error)
    {
      console.error('Error fetching alert history:', error);
    }
    finally
    {
      setHistoryLoading(false);
    }
  };

  const closeAlertHistory = (e: React.MouseEvent) =>
  {
    e.stopPropagation();
    setOpenHistoryAlert(null);
    setHistoryNotifications([]);
  };

  const handleHistoryNotificationClick = async (notification: AlertNotification, e: React.MouseEvent) =>
  {
    // Prevent the body's <a href> from navigating so only the InfoWindow opens.
    e.preventDefault();
    e.stopPropagation();
    // Summary items share one conversation, so selecting another must not toggle the InfoWindow closed
    onSelectNotification(notification, true);
    await markAsRead(notification);
    onRefreshAlerts?.();
    setTimeout(() =>
    {
      onRefreshAlerts?.();
    }, 1000);
  };

  const isDark = document.documentElement.classList.contains('dark');

  return createPortal(
    <>
      <div
        ref={containerRef}
        className={`notifications-container${showArrow ? '' : ' notifications-container--no-arrow'}`}
        style={{
          left: adjustedLeft,
          top: adjustedTop,
          ['--arrow-offset-y' as any]: `${arrowOffsetY}px`,
        }}
      >
        <div className="window-notifications">
          {/* Header */}
          <div className="notifications-header">
            <div className="notifications-header-cell">
              <div className="notifications-title">Notifications</div>
            </div>

            <span className="notification-filter-checkbox">
              <span className="corgee_com_checkbox !mb-2">
                <input
                  id="chkReadOnly"
                  type="checkbox"
                  checked={readOnly}
                  onChange={handleReadOnlyChange}
                />
                <label htmlFor="chkReadOnly" className="conversationCheckbox" />
              </span>
              <label htmlFor="chkReadOnly" className="notification-filter-label mb-0">
                Unread
              </label>
            </span>

            <span className="notification-filter-checkbox">
              <span className="corgee_com_checkbox !mb-2">
                <input
                  id="chkImportant"
                  type="checkbox"
                  checked={important}
                  onChange={handleImportantChange}
                />
                <label htmlFor="chkImportant" className="conversationCheckbox" />
              </span>
              <label htmlFor="chkImportant" className="notification-filter-label mb-0">
                Important
              </label>
            </span>

            <div className="notifications-header-cell" onClick={handleMarkAllAsRead}>
              <a className="notification-text-action">Mark all as read</a>
            </div>

            <div className="notifications-header-cell nh-icon">
              <div className="notifications-close-control" onClick={onClose}>
                <span className="material-icons">clear</span>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="corgee-notification-search">
            <input
              type="text"
              value={textSearch}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="main-search"
              placeholder="Search notifications title"
            />
            {textSearch.trim().length > 0 && (
              <div className="notifications-header-cell nh-icon">
                <div className="notifications-close-control" onClick={handleSearchClear}>
                  <span className="material-icons">clear</span>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={handleSearchSubmit}
              disabled={loading}
              aria-busy={loading}
              className="btn-search-notification"
            >
              {loading && <span className="notification-search-spinner" />}
              {loading ? 'Searching' : 'Search'}
            </button>
          </div>

          {/* Notifications List */}
          <div className="notifications-info">
            <ul className="notifications-list" ref={listRef}>
              {notifications.map(notification =>
              {
                return (
                  <li
                    key={notification.AlertID}
                    className={`notifications-all${notification.Viewed ? '' : ' notification-notread'}`}
                    onClick={e => handleNotificationClick(notification, e)}
                  >
                    <div
                      className="notification-icon"
                      style={{ paddingRight: 12, display: 'table-cell' }}
                    >
                      {notification.Icon && (
                        <span
                          className={`sidebar-item-icon ${notification.Icon}${notification.HasWarning ? ' animation-blinker' : ''}`}
                        >
                          {notification.HasWarning && (
                            <i className="material-icons warning-icon-notifications">warning</i>
                          )}
                        </span>
                      )}
                      {!notification.Icon && (
                        <span className="sidebar-item-icon icons-menu-all-inclusive" />
                      )}
                    </div>
                    <div style={{ display: 'table-cell' }} dangerouslySetInnerHTML={{ __html: removeHrefs(notification.Body) }} />
                    {notification.Counter > 1 && (
                      <div
                        className="alert-history-icon"
                        style={{ display: 'table-cell', verticalAlign: 'middle' }}
                        title="Click to view summary"
                        onClick={e => openAlertHistory(notification, e)}
                      >
                        <span
                          className="material-icons"
                          style={{
                            fontSize: 18,
                            color: notification.Viewed ? (isDark ? '#6e6860' : '#999') : '#62AACB',
                            cursor: 'pointer',
                            lineHeight: 1,
                          }}
                        >
                          chevron_right
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}

              {notifications.length === 0 && !loading && (
                <li className="notifications-no-items">
                  There isn't any notification available at the moment.
                </li>
              )}

              {noMoreItems && notifications.length > 0 && !loading && (
                <li className="notifications-no-items">
                  There are no more notifications available.
                </li>
              )}

              {loading && (
                <li className="notifications-no-items notifications-loading-row">
                  <span className="notification-search-spinner notification-search-spinner--dark" />
                  Loading notifications...
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* History Summary Panel - rendered OUTSIDE the scrolling container */}
      {openHistoryAlert && (
        <div
          ref={historyPanelRef}
          className="alert-history-panel"
          style={{
            left: historyPosition.left,
            top: historyPosition.top,
            height: historyPosition.height,
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="notifications-header">
            <div className="notifications-header-cell">
              <div className="notifications-title">
                Notifications Summary ({openHistoryAlert.Counter})
              </div>
            </div>
            <div className="notifications-header-cell nh-icon">
              <div className="notifications-close-control" onClick={closeAlertHistory}>
                <span className="material-icons">clear</span>
              </div>
            </div>
          </div>
          <div className="alerts-content">
            {historyLoading && (
              <div className="alerts-loading">
                <i className="material-icons">notifications</i>
              </div>
            )}
            <ul className="alert-history-list">
              {historyNotifications.map(historyNotification =>
              {
                return (
                  <li
                    key={historyNotification.AlertID}
                    className={`notifications-all${historyNotification.Viewed ? '' : ' notification-notread'}`}
                    onClick={e => handleHistoryNotificationClick(historyNotification, e)}
                  >
                    <div
                      className="notification-icon"
                      style={{ paddingRight: 12, display: 'table-cell' }}
                    >
                      {historyNotification.Icon ? (
                        <span
                          className={`sidebar-item-icon ${historyNotification.Icon}${historyNotification.HasWarning ? ' animation-blinker' : ''}`}
                        >
                          {historyNotification.HasWarning && (
                            <i className="material-icons warning-icon-notifications">warning</i>
                          )}
                        </span>
                      ) : (
                        <span className="sidebar-item-icon icons-menu-all-inclusive" />
                      )}
                    </div>
                    <div style={{ display: 'table-cell' }} dangerouslySetInnerHTML={{ __html: historyNotification.Body }} />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
};

export default NotificationList;
